
import { useState, useEffect } from 'react';

interface GameState {
  board: (string | null)[];
  currentPlayer: 'X' | 'O';
  winner: string | null;
  gameOver: boolean;
  scores: { X: number; O: number; draws: number };
}

export default function Game() {
  const [gameState, setGameState] = useState<GameState>({
    board: Array(9).fill(null),
    currentPlayer: 'X',
    winner: null,
    gameOver: false,
    scores: { X: 0, O: 0, draws: 0 }
  });

  const [customImages, setCustomImages] = useState({
    X: 'https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/402272145bb42e284dcf8e5ab3a938be.png',
    O: 'https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/2e890e69e3799441b4394e594f62642c.png'
  });

  const [showImageUpload, setShowImageUpload] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  // 添加 AudioContext 狀態管理
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  // 初始化 AudioContext
  const initAudioContext = () => {
    if (!audioContext || audioContext.state === 'closed') {
      try {
        const newAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        setAudioContext(newAudioContext);
        
        // 確保 AudioContext 處於 running 狀態
        if (newAudioContext.state === 'suspended') {
          newAudioContext.resume();
        }
        
        return newAudioContext;
      } catch (error) {
        console.log('AudioContext 初始化失敗:', error);
        return null;
      }
    }
    
    // 確保現有的 AudioContext 處於 running 狀態
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    return audioContext;
  };

  // 下棋音效 - 清脆的點擊聲
  const playMoveSound = () => {
    if (!soundEnabled) return;
    
    const ctx = initAudioContext();
    if (!ctx) return;
    
    try {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);

      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.1);
    } catch (error) {
      console.log('音效播放失敗:', error);
    }
  };

  // 勝利音效 - 歡快的旋律
  const playWinSound = () => {
    if (!soundEnabled) return;
    
    const ctx = initAudioContext();
    if (!ctx) return;
    
    try {
      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6

      notes.forEach((frequency, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + index * 0.15);
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, ctx.currentTime + index * 0.15);
        gainNode.gain.linearRampToValueAtTime(0.2, ctx.currentTime + index * 0.15 + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + index * 0.15 + 0.3);

        oscillator.start(ctx.currentTime + index * 0.15);
        oscillator.stop(ctx.currentTime + index * 0.15 + 0.3);
      });
    } catch (error) {
      console.log('音效播放失敗:', error);
    }
  };

  // 和局音效 - 中性的提示音
  const playDrawSound = () => {
    if (!soundEnabled) return;
    
    const ctx = initAudioContext();
    if (!ctx) return;
    
    try {
      const frequencies = [440, 440, 440]; // A4音符重複三次

      frequencies.forEach((frequency, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + index * 0.2);
        oscillator.type = 'square';

        gainNode.gain.setValueAtTime(0, ctx.currentTime + index * 0.2);
        gainNode.gain.linearRampToValueAtTime(0.15, ctx.currentTime + index * 0.2 + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + index * 0.2 + 0.15);

        oscillator.start(ctx.currentTime + index * 0.2);
        oscillator.stop(ctx.currentTime + index * 0.2 + 0.15);
      });
    } catch (error) {
      console.log('音效播放失敗:', error);
    }
  };

  const checkWinner = (board: (string | null)[]): string | null => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];

    for (const [a, b, c] of lines) {
      if (board[a] && board[a] === board[b] && board[a] === board[c]) {
        return board[a];
      }
    }

    return board.every(cell => cell !== null) ? 'draw' : null;
  };

  const handleCellClick = (index: number) => {
    if (gameState.board[index] || gameState.gameOver) return;

    // 播放下棋音效
    playMoveSound();

    const newBoard = [...gameState.board];
    newBoard[index] = gameState.currentPlayer;

    const winner = checkWinner(newBoard);
    const isGameOver = winner !== null;

    let newScores = { ...gameState.scores };
    if (winner === 'X') newScores.X++;
    else if (winner === 'O') newScores.O++;
    else if (winner === 'draw') newScores.draws++;

    setGameState({
      board: newBoard,
      currentPlayer: gameState.currentPlayer === 'X' ? 'O' : 'X',
      winner,
      gameOver: isGameOver,
      scores: newScores
    });

    // 延遲播放結果音效，讓下棋音效先播放完
    if (isGameOver) {
      setTimeout(() => {
        if (winner === 'draw') {
          playDrawSound();
        } else {
          playWinSound();
        }
      }, 200);
    }
  };

  const resetGame = () => {
    // 重新開始時重新初始化 AudioContext
    initAudioContext();
    
    setGameState({
      ...gameState,
      board: Array(9).fill(null),
      currentPlayer: 'X',
      winner: null,
      gameOver: false
    });
  };

  const resetScores = () => {
    setGameState({
      ...gameState,
      scores: { X: 0, O: 0, draws: 0 }
    });
  };

  // 清理 AudioContext
  useEffect(() => {
    return () => {
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }
    };
  }, [audioContext]);

  const handleImageUpload = (player: 'X' | 'O', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomImages(prev => ({
          ...prev,
          [player]: e.target?.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const renderCell = (index: number) => {
    const value = gameState.board[index];
    if (!value) return null;

    if (customImages[value as 'X' | 'O']) {
      return (
        <img
          src={customImages[value as 'X' | 'O']}
          alt={value}
          className="w-16 h-16 object-contain rounded-lg"
        />
      );
    }

    return (
      <div className={`text-4xl font-bold ${value === 'X' ? 'text-red-500' : 'text-blue-500'}`}>
        {value === 'X' ? '😈' : '😇'}
      </div>
    );
  };

  const getStatusMessage = () => {
    if (gameState.winner === 'draw') return '平局！';
    if (gameState.winner) {
      const winnerName = gameState.winner === 'X' ? '惡魔' : '天使';
      return `${winnerName} 獲勝！`;
    }
    const currentName = gameState.currentPlayer === 'X' ? '惡魔' : '天使';
    return `輪到 ${currentName}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-sm mx-auto">
        {/* 標題 - 更緊湊設計 */}
        <div className="text-center mb-4">
          {/* Logo - 縮小尺寸 */}
          <div className="mb-2">
            <img
              src="https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/b050347aa2ffd80a536cc0e14bac559a.png"
              alt="Noir Blanc Logo"
              className="w-16 h-16 mx-auto rounded-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: '"Pacifico", serif' }}>
            惡魔與天使
          </h1>
          <p className="text-lg text-purple-200">井之較量</p>
        </div>

        {/* 計分板 - 更緊湊設計 */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="flex items-center justify-center space-x-2">
              <div>
                {customImages.X ? (
                  <img
                    src={customImages.X}
                    alt="惡魔"
                    className="w-8 h-8 object-contain rounded-lg"
                  />
                ) : (
                  <div className="text-lg">😈</div>
                )}
              </div>
              <div>
                <div className="text-white font-bold text-lg">{gameState.scores.X}</div>
                <div className="text-purple-200 text-xs">惡魔</div>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div className="text-lg">🤝</div>
              <div>
                <div className="text-white font-bold text-lg">{gameState.scores.draws}</div>
                <div className="text-purple-200 text-xs">平局</div>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div>
                {customImages.O ? (
                  <img
                    src={customImages.O}
                    alt="天使"
                    className="w-8 h-8 object-contain rounded-lg"
                  />
                ) : (
                  <div className="text-lg">😇</div>
                )}
              </div>
              <div>
                <div className="text-white font-bold text-lg">{gameState.scores.O}</div>
                <div className="text-purple-200 text-xs">天使</div>
              </div>
            </div>
          </div>
        </div>

        {/* 遊戲狀態 - 縮小30% */}
        <div className="text-center mb-4">
          <div
            className={`text-sm font-bold px-2 py-1 rounded-full inline-block ${
              gameState.gameOver
                ? 'bg-yellow-500 text-black'
                : gameState.currentPlayer === 'X'
                ? 'bg-red-500 text-white'
                : 'bg-blue-500 text-white'
            }`}
          >
            {getStatusMessage()}
          </div>
        </div>

        {/* 遊戲棋盤 - 位置提高 */}
        <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
          <div className="grid grid-cols-3 gap-3">
            {gameState.board.map((_, index) => (
              <button
                key={index}
                onClick={() => handleCellClick(index)}
                className="aspect-square bg-white/30 rounded-xl flex items-center justify-center hover:bg-white/40 transition-all duration-200 active:scale-95"
                disabled={gameState.gameOver || gameState.board[index] !== null}
              >
                {renderCell(index)}
              </button>
            ))}
          </div>
        </div>

        {/* 控制按鈕 */}
        <div className="space-y-3">
          <button
            onClick={resetGame}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200"
          >
            <i className="ri-refresh-line mr-2"></i>
            重新開始
          </button>

          <button
            onClick={() => setShowImageUpload(!showImageUpload)}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200"
          >
            <i className="ri-image-line mr-2"></i>
            自定義圖片
          </button>

          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`w-full font-bold py-3 px-6 rounded-xl transition-colors duration-200 ${
              soundEnabled
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : 'bg-gray-500 hover:bg-gray-600 text-white'
            }`}
          >
            <i className={`${soundEnabled ? 'ri-volume-up-line' : 'ri-volume-mute-line'} mr-2`}></i>
            {soundEnabled ? '關閉音效' : '開啟音效'}
          </button>

          <button
            onClick={resetScores}
            className="w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200"
          >
            <i className="ri-delete-bin-line mr-2"></i>
            重置計分
          </button>
        </div>

        {/* 圖片上傳區域 */}
        {showImageUpload && (
          <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="text-white font-bold mb-4 text-center">上傳自定義圖片</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-purple-200 mb-2">惡魔圖片 (X)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload('X', e)}
                  className="w-full text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-500 file:text-white hover:file:bg-purple-600"
                />
                {customImages.X && (
                  <div className="mt-2 flex justify-center">
                    <img src={customImages.X} alt="惡魔預覽" className="w-16 h-16 object-contain rounded-lg" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-purple-200 mb-2">天使圖片 (O)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload('O', e)}
                  className="w-full text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                />
                {customImages.O && (
                  <div className="mt-2 flex justify-center">
                    <img src={customImages.O} alt="天使預覽" className="w-16 h-16 object-contain rounded-lg" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 遊戲說明 */}
        <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-2xl p-4">
          <h3 className="text-white font-bold mb-2">遊戲說明</h3>
          <ul className="text-purple-200 text-sm space-y-1">
            <li>• 惡魔先手，天使後手</li>
            <li>• 連成三個即可獲勝</li>
            <li>• 可上傳自己的圖片替換默認圖標</li>
            <li>• 支持計分和重新開始</li>
            <li>• 內建音效：下棋聲、勝利聲、和局聲</li>
            <li>• 可隨時開啟或關閉音效</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
